// src/auth/OtpVerify.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import MessageModal from '../MessageModal';
import '../auth/Login.css'; // 既存スタイルを共用

const DEFAULT_RESEND_SECONDS = 60;

const OtpVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = import.meta.env.VITE_API_URL;

  // ---- state を優先し、無ければ sessionStorage から復元 ----
  const initialState = (() => {
    if (location.state) return location.state;
    try {
      const raw = sessionStorage.getItem('otpFlowState');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  })() || {};

  // 呼び出し元から受け取る共通パラメータ
  const {
    challengeId,
    userAddress,
    maskedEmail,
    ttlSec,
    resendCooldownSec,

    // 戻り方指定（どれかを渡すだけでOK）
    nextTo,          // 例: '/admin/dashboard'（単純に進む）
    returnTo,        // 例: '/forgot-password'（元画面に戻って続行）
    returnState,     // 例: { otpVerified: true, email }
    afterOtpAction,  // 例: { type: 'password_reset_request', email, navigateTo? }
  } = initialState;

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [resendLeft, setResendLeft] = useState(resendCooldownSec ?? DEFAULT_RESEND_SECONDS);
  const [ttlLeft, setTtlLeft] = useState(ttlSec ?? 0);

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalConfig, setMessageModalConfig] = useState({});
  const openModal = (config) => {
    setMessageModalConfig({
      confirmText: '閉じる',
      onConfirm: closeModal,
      onClose: closeModal,
      hideButtons: false,
      ...config,
    });
    setMessageModalOpen(true);
  };
  const closeModal = () => setMessageModalOpen(false);

  const canSubmit = useMemo(() => /^\d{6}$/.test(code) && !submitting, [code, submitting]);


  // state も復元も無ければログインへ
  useEffect(() => {
    if (!challengeId) {
      navigate('/login', { replace: true });
    }
  }, [challengeId, navigate]);

  // TTL / 再送 カウントダウン
  useEffect(() => {
    let timerId;
    if (challengeId) {
      setResendLeft(resendCooldownSec ?? DEFAULT_RESEND_SECONDS);
      if (ttlSec) setTtlLeft(ttlSec);
      timerId = setInterval(() => {
        setResendLeft((s) => (s > 0 ? s - 1 : 0));
        setTtlLeft((t) => (t > 0 ? t - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timerId);
  }, [challengeId, ttlSec, resendCooldownSec]);

  const onChangeCode = (e) => {
    const v = e.target.value.replace(/[^\d]/g, '').slice(0, 6);
    setCode(v);
    setErrorText('');
  };

  // ---- OTP成功後の共通ディスパッチ ----
  const proceedAfterOtp = async () => {
    // 使い終わった復元データは掃除
    sessionStorage.removeItem('otpFlowState');

    // 1) まず「後続APIを叩く」系（例：パスワードリセット要求）
    if (afterOtpAction?.type === 'password_reset_request') {
      try {
        await axios.post(
          `${API_URL}/api/password-reset-requests/`,
          { WAMID: afterOtpAction.email || userAddress }
        );
        // navigate先が指定されていれば優先、それ以外は固定の完了画面へ
        navigate(afterOtpAction.navigateTo || '/password-reset-sent', { replace: true });
        return;
      } catch (e) {
        console.error('password-reset-requests error', e?.response?.data || e?.message);
        setErrorText('リセットリンクの送信に失敗しました。時間をおいて再度お試しください。');
        return;
      }
    }

    // 2) 元の画面へ戻って続行（state を付けて戻る）
    if (returnTo) {
      navigate(returnTo, { state: returnState, replace: true });
      return;
    }

    // 3) 単純にどこかへ進む（管理者ログインなど）
    if (nextTo) {
      navigate(nextTo, { replace: true });
      return;
    }

    // 4) 何も指定が無ければトップへ
    navigate('/', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setErrorText('');

    try {
      const res = await axios.post(
        `${API_URL}/api/otp/verify`,
        { challengeId, otp: code }
      );

      if (res.data?.ok) {
        // 判定だけ。保存はしない。戻り先は state で決める。
        await proceedAfterOtp();
      } else {
        setErrorText('ワンタイムパスが正しくありません。もう一度入力してください。');
      }
    } catch (error) {
      console.error('status', error.response?.status);
      console.error('data', error.response?.data);
      if (error.response?.status === 429) {
        const retry = parseInt(error.response?.headers?.['retry-after'] ?? '10', 10);
        if (retry > 0) {
          setResendLeft(retry);
          setErrorText(`操作が集中しています。${retry}秒後に再送できます。`);
        } else {
          setErrorText('操作が集中しています。しばらくしてからお試しください。');
        }
      } else if (error.response?.status === 400) {
        setErrorText('ワンタイムパスが正しくありません。もう一度入力してください。');
      } else if (error.message === 'Network Error') {
        setErrorText('ネットワークエラーが発生しました。時間をおいて再度お試しください。');
      } else {
        setErrorText('確認中にエラーが発生しました。再度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendLeft > 0 || !challengeId) return;
    try {
      
      await axios.post(
        `${API_URL}/api/otp/resend`,
        { challengeId }
      );
      openModal({ message: '認証コードを再送しました。届くまで数十秒かかる場合があります。' });
      setResendLeft(resendCooldownSec ?? DEFAULT_RESEND_SECONDS);
    } catch (e) {
      const retry = parseInt(e?.response?.headers?.['retry-after'] ?? '0', 10);
      if (e?.response?.status === 429 && retry > 0) {
        setResendLeft(retry);
        setErrorText(`操作が集中しています。${retry}秒後に再送できます。`);
        return;
      }
      setErrorText('再送に失敗しました。時間をおいて再度お試しください。');
    }
  };

  const formatMMSS = (sec) => {
    const m = Math.floor((sec || 0) / 60);
    const s = (sec || 0) % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="login-container">
      <div className="login-card" role="dialog" aria-labelledby="otp-title">
        <h2 id="otp-title" className="login-title">ワンタイムパス確認</h2>
        <p className="login-subtitle">
          {maskedEmail
            ? <>メールアドレス <strong>{maskedEmail}</strong> に送信した6桁コードを入力してください。</>
            : <>メールアドレス <strong>{userAddress}</strong> に送信した6桁コードを入力してください。</>
          }
          {ttlLeft > 0 && <>（有効期限 残り <strong>{formatMMSS(ttlLeft)}</strong>）</>}
        </p>

        {errorText && <div className="error-alert" role="alert">{errorText}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="otpCode">6桁コード</label>
            <input
              id="otpCode"
              inputMode="numeric"
              pattern="\d{6}"
              autoComplete="one-time-code"
              type="text"
              value={code}
              onChange={onChangeCode}
              placeholder="123456"
              aria-invalid={!!errorText}
              maxLength={6}
            />
            <div className="otp-hint" aria-hidden="true">半角数字6桁</div>
          </div>

          <button type="submit" className={`login-button ${submitting ? 'is-loading' : ''}`} disabled={!canSubmit}>
            {submitting ? '確認中…' : '確認する'}
          </button>
        </form>

        <div className="link-row" style={{ marginTop: 12 }}>
          コードが届きませんか？{' '}
          <button
            type="button"
            className="link"
            onClick={handleResend}
            disabled={resendLeft > 0}
            aria-disabled={resendLeft > 0}
            style={{ background: 'none', border: 'none', padding: 0, cursor: resendLeft > 0 ? 'not-allowed' : 'pointer' }}
          >
            {resendLeft > 0 ? `再送可能まで ${resendLeft}秒` : 'コードを再送'}
          </button>
        </div>

        <div className="link-row" style={{ marginTop: 6 }}>
          <Link to="/login" className="link">ログインに戻る</Link>
        </div>
      </div>

      {messageModalOpen && <MessageModal {...messageModalConfig} />}
    </div>
  );
};

export default OtpVerify;
